/**
 * Sidebar — flat IA per Part 10.5. Role-agnostic; CEO is implicit.
 */
import React, { useState } from 'react';
import { tokensV2 as T } from './tokens.v2';

/**
 * Square brand mark for the sidebar header. Renders the Fancall lockup PNG
 * cropped to its top region (the star + phone mark, no wordmark) at 34×34.
 * Falls back to an orange-gradient F tile if the PNG isn't deployed yet.
 */
function SidebarLogoMark() {
  const [imgFailed, setImgFailed] = useState(false);
  if (imgFailed) {
    return (
      <div style={{
        width: 34, height: 34, borderRadius: 10,
        background: `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#FFFFFF', fontWeight: 800, fontSize: 16,
      }}>F</div>
    );
  }
  // The lockup PNG (~2842×2948) stacks: star+phone mark on top, wordmark
  // middle, tagline bottom. For the sidebar we want only the star/phone mark
  // floating on the sidebar's white surface — no rounded tile, no border.
  // Math: image rendered at width 64 → natural ratio ~0.96 → height ≈ 66px.
  // Wrapper clips to 38px tall, showing the top ~58% of the image which is
  // exactly the star. The clipping is done with overflow:hidden but no
  // background colour so the star sits flush on the sidebar surface.
  return (
    <div style={{
      width: 40, height: 38,
      overflow: 'hidden', position: 'relative',
      flexShrink: 0,
    }}>
      <img
        src="/fancall-logo.png"
        alt="Fancall"
        onError={() => setImgFailed(true)}
        style={{
          position: 'absolute',
          top: 0, left: '50%',
          width: 64, height: 'auto',
          transform: 'translateX(-50%)',
          display: 'block',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

// Pages backed by real data. Reports / Saved templates / Report history / Settings
// were left as design mocks during v2 buildout — they're hidden from launch nav so
// the CEO never sees fabricated rows. Re-enable each id here once the page is wired
// to a live endpoint.
const NAV = [
  { id: 'home',         label: 'Home',          group: 'main' },
  { id: 'fans',         label: 'Fans',          group: 'main' },
  { id: 'creators',     label: 'Creators',      group: 'main' },
  { id: 'brands',       label: 'Brands',        group: 'main' },
  { id: 'money',        label: 'Money',         group: 'main' },
  { id: 'operations',   label: 'Operations',    group: 'main' },
  { id: 'acquisition',  label: 'Acquisition',   group: 'main' },
  { id: 'audit',        label: 'Audit & Tax',   group: 'main' },

  { id: 'investor',     label: 'Investor data', group: 'reports' },
  { id: 'relational',   label: 'Relational reports', group: 'reports' },
  { id: 'custom',       label: 'Build your own', group: 'reports' },
  { id: 'settings',     label: 'Settings & access', group: 'meta' },
];

function NavItem({ item, active, onClick }) {
  const isActive = item.id === active;
  return (
    <button
      type="button"
      onClick={() => { onClick(item.id); }}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '10px 14px',
        background: isActive ? T.brand.soft : 'transparent',
        color: isActive ? T.brand.base : T.text.secondary,
        border: 'none',
        borderRadius: T.radius.md,
        fontSize: 14,
        fontWeight: isActive ? 700 : 600,
        fontFamily: T.font.sans,
        cursor: 'pointer',
        outline: 'none',
        transition: 'color 120ms ease, background-color 120ms ease',
      }}
    >
      {item.label}
    </button>
  );
}

export function Sidebar({ active = 'home', onSelect = () => {}, isMobile = false, isOpen = true, onClose = () => {} }) {
  const main    = NAV.filter(n => n.group === 'main');
  const reports = NAV.filter(n => n.group === 'reports');
  const meta    = NAV.filter(n => n.group === 'meta');

  if (isMobile && !isOpen) return null;

  const aside = (
    <aside style={{
      width: 240, minWidth: 240,
      borderRight: `1px solid ${T.border.subtle}`,
      backgroundColor: T.surface.card,
      padding: 16,
      display: 'flex', flexDirection: 'column', gap: 16,
      overflow: 'hidden',
      fontFamily: T.font.sans,
      ...(isMobile ? {
        position: 'fixed', top: 0, left: 0, bottom: 0,
        zIndex: 50,
        boxShadow: '4px 0 24px rgba(31,42,63,0.14)',
      } : {
        position: 'sticky', top: 0, height: '100vh', alignSelf: 'flex-start',
      }),
    }}>
      <div style={{ height: 65, marginLeft: -16, marginRight: -16, marginTop: -16, padding: '0 16px', borderBottom: `1px solid ${T.border.subtle}`, display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SidebarLogoMark />
          <div>
            <div style={{ color: T.text.primary, fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>Fancall</div>
            <div style={{ color: T.text.muted, fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase' }}>CEO Dashboard</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {main.map(it => <NavItem key={it.id} item={it} active={active} onClick={onSelect} />)}
      </div>

      <div style={{ borderTop: `1px solid ${T.border.subtle}`, paddingTop: 12 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
          textTransform: 'uppercase', color: T.text.muted,
          padding: '0 14px 8px',
        }}>Reports</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {reports.map(it => <NavItem key={it.id} item={it} active={active} onClick={onSelect} />)}
        </div>
      </div>

      {meta.length > 0 && (
        <div style={{ marginTop: 'auto', borderTop: `1px solid ${T.border.subtle}`, paddingTop: 12 }}>
          {meta.map(it => <NavItem key={it.id} item={it} active={active} onClick={onSelect} />)}
        </div>
      )}
    </aside>
  );

  if (!isMobile) return aside;
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(31,42,63,0.50)',
          zIndex: 40,
        }}
        aria-hidden
      />
      {aside}
    </>
  );
}
